import React from "react";
import { DeskPill, DeskRowMenu, DeskTable } from "../desktop/DeskTable";
import { formatCurrency } from "../../helpers/estimateTotals";
import {
  computeReviewLineTotal,
  LINE_STATUS,
  PRICING_SOURCE,
} from "../../helpers/invoiceReview";
import { getInvoiceLineTypeMeta } from "../../helpers/invoiceLineTypes";

/** Same recompute rules as the stacked editor row (qty / rate / discount → total). */
export function applyReviewLinePatch(line, patch) {
  const next = { ...line, ...patch };
  if ("unit_amount" in patch || "discount_percent" in patch || "quantity" in patch) {
    next.line_total = computeReviewLineTotal(next.quantity, next.unit_amount, next.discount_percent);
    if ("unit_amount" in patch) {
      next.pricing_source = PRICING_SOURCE.MANUAL_OVERRIDE;
    }
  }
  return next;
}

/**
 * Desktop invoice-review line editor: one table row per line with inline
 * qty / unit / rate / discount inputs. State lives in InvoiceReviewScreen.
 */
export function InvoiceReviewLinesDesktop({
  lines,
  disabled,
  selectedIds,
  expandedIds,
  allSelected,
  onToggleAll,
  onToggleSelect,
  onToggleExpand,
  onChange,
  onRemove,
  onMove,
}) {
  const indexOf = (line) => lines.findIndex((row) => row.line_id === line.line_id);

  const columns = [
    {
      key: "select",
      label: (
        <input
          type="checkbox"
          className="inv-check"
          checked={allSelected}
          disabled={disabled || lines.length === 0}
          onChange={onToggleAll}
          aria-label="Select all lines"
        />
      ),
      width: "46px",
      className: "dt-cell--actions",
      render: (line) => (
        <input
          type="checkbox"
          className="inv-check"
          checked={!!selectedIds[line.line_id]}
          disabled={disabled}
          onChange={() => onToggleSelect(line.line_id)}
          aria-label={`Select ${line.description || "line"}`}
        />
      ),
    },
    {
      key: "item",
      label: "Item",
      width: "27%",
      render: (line) => {
        const removed = line.line_status === LINE_STATUS.REMOVED;
        const lock = disabled || removed;
        const meta = getInvoiceLineTypeMeta(line.line_type);
        return (
          <span className="inv-line-item">
            <input
              type="text"
              className="inv-cell-input inv-cell-input--strong"
              value={line.description ?? ""}
              disabled={lock}
              aria-label="Line name"
              onChange={(e) => onChange(line, applyReviewLinePatch(line, { description: e.target.value }))}
            />
            <input
              type="text"
              className="inv-cell-input"
              value={line.detail ?? ""}
              disabled={lock}
              placeholder="Description"
              aria-label="Line description"
              onChange={(e) => onChange(line, applyReviewLinePatch(line, { detail: e.target.value || null }))}
            />
            <span className="inv-line-item__meta">
              <DeskPill tone="muted">{meta.label || line.line_type || "Line"}</DeskPill>
              {removed ? <DeskPill tone="red">Removed</DeskPill> : null}
              {line.line_type === "service" ? (
                <label className="inv-line-item__tax">
                  <input
                    type="checkbox"
                    className="inv-check"
                    checked={line.metadata?.taxable !== false}
                    disabled={lock}
                    onChange={(e) =>
                      onChange(
                        line,
                        applyReviewLinePatch(line, {
                          metadata: { ...(line.metadata || {}), taxable: e.target.checked },
                        }),
                      )
                    }
                  />
                  Taxable
                </label>
              ) : null}
            </span>
            {expandedIds[line.line_id] ? (
              <span className="inv-line-item__refs">
                <span>Cost reference: {formatCurrency(line.reference_unit_cost)}</span>
                <span>
                  Quoted reference:{" "}
                  {line.reference_quoted_unit_price != null ? formatCurrency(line.reference_quoted_unit_price) : "—"}
                </span>
                <span>
                  Catalog sell reference:{" "}
                  {line.reference_catalog_sell != null ? formatCurrency(line.reference_catalog_sell) : "—"}
                </span>
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: "qty",
      label: "Qty",
      width: "12%",
      render: (line) => (
        <input
          type="number"
          min="0"
          step="0.01"
          className="inv-cell-input inv-cell-input--num"
          value={line.quantity ?? ""}
          disabled={disabled || line.line_status === LINE_STATUS.REMOVED}
          aria-label="Quantity"
          onChange={(e) => onChange(line, applyReviewLinePatch(line, { quantity: Number(e.target.value) || 0 }))}
        />
      ),
    },
    {
      key: "unit",
      label: "Unit",
      width: "11%",
      render: (line) => (
        <input
          type="text"
          className="inv-cell-input"
          value={line.unit_label ?? ""}
          disabled={disabled || line.line_status === LINE_STATUS.REMOVED}
          aria-label="Unit"
          onChange={(e) => onChange(line, applyReviewLinePatch(line, { unit_label: e.target.value }))}
        />
      ),
    },
    {
      key: "rate",
      label: "Bill Rate",
      width: "15%",
      render: (line) => (
        <input
          type="number"
          min="0"
          step="0.01"
          className="inv-cell-input inv-cell-input--num"
          value={line.unit_amount ?? ""}
          disabled={disabled || line.line_status === LINE_STATUS.REMOVED}
          aria-label="Bill rate"
          onChange={(e) => onChange(line, applyReviewLinePatch(line, { unit_amount: Number(e.target.value) || 0 }))}
        />
      ),
    },
    {
      key: "disc",
      label: "Disc %",
      width: "12%",
      render: (line) => (
        <input
          type="number"
          min="0"
          max="100"
          step="0.1"
          className="inv-cell-input inv-cell-input--num"
          value={line.discount_percent ?? 0}
          disabled={disabled || line.line_status === LINE_STATUS.REMOVED}
          aria-label="Discount percent"
          onChange={(e) => onChange(line, applyReviewLinePatch(line, { discount_percent: Number(e.target.value) || 0 }))}
        />
      ),
    },
    {
      key: "total",
      label: "Total",
      align: "right",
      width: "14%",
      render: (line) => <span className="dt-num">{formatCurrency(line.line_total)}</span>,
    },
    {
      key: "menu",
      label: "",
      width: "52px",
      className: "dt-cell--actions",
      render: (line) => {
        const index = indexOf(line);
        const removed = line.line_status === LINE_STATUS.REMOVED;
        const items = [];
        if (!disabled && !removed && index > 0) items.push({ key: "up", label: "Move up" });
        if (!disabled && !removed && index < lines.length - 1) items.push({ key: "down", label: "Move down" });
        items.push({ key: "refs", label: expandedIds[line.line_id] ? "Hide references" : "Show references" });
        if (!disabled) items.push({ key: "remove", label: "Remove from invoice", danger: true });
        return (
          <DeskRowMenu
            items={items}
            label="Line actions"
            onSelect={(item) => {
              if (item.key === "up") onMove(line, "up");
              else if (item.key === "down") onMove(line, "down");
              else if (item.key === "refs") onToggleExpand(line.line_id);
              else if (item.key === "remove") onRemove(line.line_id);
            }}
          />
        );
      },
    },
  ];

  return (
    <DeskTable
      testId="invoice-review-lines"
      caption="Invoice line items"
      className="inv-flat-table inv-edit-table"
      columns={columns}
      rows={lines}
      rowKey={(line) => line.line_id}
      rowClassName={(line) => (line.line_status === LINE_STATUS.REMOVED ? "is-muted" : "")}
    />
  );
}
